import type { StoryMod } from '@goodtime/shared';
import { MOD_INFO, modTitle } from './mods';
import { mapPieceLine } from './visitors';

/**
 * Fen the fox, the road's host: what he says, and when. Pure logic, so the
 * rules (chatter levels, no repeats, one line every other spin) are tested
 * without a canvas.
 *
 * Copy rules: plain British English, no betting words, no near-miss lines
 * ("so close"), and every ending kind.
 */

/** Fen's poses (art in avatar/friendly_fox). */
export type FenPose = 'idle' | 'talking' | 'cheering' | 'worried';

/** `big` moments always speak; `often` ones only for players who chose
 *  "Often" in Settings, and at most every other spin. */
export type FenLevel = 'big' | 'often';

export type FenEvent =
  | { kind: 'welcome' }
  | { kind: 'newRoad'; themeId: string }
  | { kind: 'back' }
  | { kind: 'firstCollect' }
  | { kind: 'bigWin' }
  | { kind: 'quiet' }
  | { kind: 'runStart'; origin: 'trail' | 'ready' }
  | { kind: 'runCollected'; paid: boolean }
  | { kind: 'mod'; mod: StoryMod; spins: number }
  | { kind: 'box' }
  | { kind: 'boxOpened'; coins: number; gems: number; freeSpins: number }
  | { kind: 'rival' }
  | { kind: 'mapPiece'; pieces: number; of: number }
  | { kind: 'visitorSentOn'; name: string }
  | { kind: 'visitorPlayed'; name: string };

export interface FenLine {
  text: string;
  pose: FenPose;
  level: FenLevel;
  /** Which line it was, so the next pick can avoid it. */
  key: string;
}

/** Words that change per slot and per name. */
export interface FenContext {
  /** Plural, lower case: "flowers". */
  collectorNoun: string;
  /** The run's player-facing name (one constant, RUN_NAME). */
  runName: string;
  /** The slot's name: "Whispering Woods". */
  slotName: string;
}

/** Each road's trouble and the place restoring it mends, for Fen's hello on
 *  a player's first visit to a road after their first (his welcome covers
 *  that one). */
const NEW_ROAD: Record<string, { trouble: string; place: string }> = {
  forest: { trouble: 'The wood has gone quiet and grey.', place: 'the glade' },
  royal: { trouble: 'The Crown is lost and the castle has fallen silent.', place: 'the castle' },
  underwater: { trouble: 'A storm has wrecked the cove.', place: 'the shore' },
  frost: { trouble: 'A big freeze has put out the lights over the peak.', place: 'the mountain' },
  frontier: { trouble: 'The gold ran out and Dustwood dozed off.', place: 'the town' },
  neon: { trouble: 'A power cut has left the city dark.', place: 'the lights' },
  caravan: { trouble: 'The sun fell from the Sun Gate and the caravan lost its way.', place: 'the road' },
  sherbet_square: { trouble: "The fountain's gone flat.", place: 'the square' },
};

const pick = <T,>(options: readonly T[], r: number): T => options[Math.min(options.length - 1, Math.floor(r * options.length))]!;

/** Every line Fen can say for an event (the picker chooses one). */
export function fenLines(event: FenEvent, c: FenContext): FenLine[] {
  const line = (key: string, text: string, pose: FenPose, level: FenLevel): FenLine => ({ key, text, pose, level });
  switch (event.kind) {
    case 'welcome':
      return [line('welcome', `Welcome to ${c.slotName}! I'm Fen. Spin the reels, collect ${c.collectorNoun}, and I'll show you round.`, 'talking', 'big')];
    case 'newRoad': {
      const road = NEW_ROAD[event.themeId];
      if (!road) return [];
      const text = `Welcome to ${c.slotName}! ${road.trouble} Restore ${road.place} and four new friends will join you.`;
      return [line(`newRoad:${event.themeId}`, text, 'talking', 'big')];
    }
    case 'back':
      return [line('back', "I'm back! Did you miss me?", 'cheering', 'big')];
    case 'firstCollect':
      return [line('firstCollect', `See the ${c.collectorNoun}? Each one moves you along the trail.`, 'talking', 'often')];
    case 'bigWin':
      return [
        line('bigWin1', 'Now that is a win!', 'cheering', 'often'),
        line('bigWin2', 'Look at all those coins!', 'cheering', 'often'),
      ];
    case 'quiet':
      return [line('quiet', `Every spin lights a side light. Fill all ten for a ${c.runName}.`, 'talking', 'often')];
    case 'runStart':
      return event.origin === 'trail'
        ? [line('runTrail', `End of the trail! ${c.runName} time.`, 'cheering', 'big')]
        : [line('runReady', `The side lights are full! ${c.runName} time.`, 'cheering', 'big')];
    case 'runCollected':
      return event.paid
        ? [line('runPaid', "Lovely! That's all in your wallet.", 'cheering', 'often')]
        : [line('runEmpty', `No prizes that time. The ${c.runName} will be back soon.`, 'worried', 'often')];
    case 'mod': {
      const info = MOD_INFO[event.mod];
      return [line(`mod:${event.mod}`, `${titleCase(modTitle(event.mod, c.collectorNoun))}! ${info.what(event.spins, c.collectorNoun)}`, 'cheering', 'big')];
    }
    case 'box':
      return [line('box', 'A mystery box just landed! Tap it to see what is inside.', 'talking', 'big')];
    case 'boxOpened':
      if (event.coins > 0) return [line('boxCoins', `${event.coins.toLocaleString('en-GB')} coins! Into your wallet they go.`, 'cheering', 'often')];
      if (event.freeSpins > 0) return [line('boxSpins', `${event.freeSpins} free spins! Enjoy them.`, 'cheering', 'often')];
      return [line('boxGems', `${event.gems} gems! Lovely and shiny.`, 'cheering', 'often')];
    case 'rival':
      return [line('rival', 'Oh look, it is Bea. She plays here too.', 'talking', 'often')];
    case 'mapPiece':
      return [line(`mapPiece:${event.pieces}`, mapPieceLine(event.pieces, event.of, true), 'cheering', 'big')];
    case 'visitorSentOn':
      return [line('visitorSentOn', `No problem. ${event.name} will pop by another time.`, 'talking', 'often')];
    case 'visitorPlayed':
      return [line('visitorPlayed', `Thanks for playing with ${event.name}!`, 'cheering', 'often')];
  }
}

function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** What the picker remembers between lines. */
export interface FenMemory {
  lastKey: string | null;
  /** Paid spin count when Fen last spoke at the `often` level. */
  lastOftenSpin: number;
}

export const FEN_FRESH: FenMemory = { lastKey: null, lastOftenSpin: -99 };

/**
 * The line Fen says for an event now, or null when he stays quiet: the
 * story is off, the line is chatter and the player chose big moments only,
 * or he spoke chatter on the last spin. Never the same line twice running
 * when there's another to say.
 */
export function chooseFenLine(
  event: FenEvent,
  c: FenContext,
  opts: { storyOn: boolean; chatter: FenLevel; spin: number; memory: FenMemory; random?: number },
): { line: FenLine; memory: FenMemory } | null {
  if (!opts.storyOn) return null;
  const all = fenLines(event, c);
  const level = all[0]?.level;
  if (!level) return null;
  if (level === 'often') {
    if (opts.chatter !== 'often') return null;
    if (opts.spin - opts.memory.lastOftenSpin < 2) return null;
  }
  const fresh = all.filter((l) => l.key !== opts.memory.lastKey);
  const line = pick(fresh.length > 0 ? fresh : all, opts.random ?? Math.random());
  return {
    line,
    memory: { lastKey: line.key, lastOftenSpin: level === 'often' ? opts.spin : opts.memory.lastOftenSpin },
  };
}
